import {
  countryToCurrencyCode,
  countryToCurrencySymbol,
  currencyCodeToSymbol,
} from '@root/shared/currencyByCountry';
import {
  AmenityKey,
  BuildingAmenityDescription,
  CurrentUser,
  ListingAmenityDescription,
} from '@root/types';

const dateRegex = /^\d{2}\/\d{2}\/\d{4}/;

type AmenityDescription = BuildingAmenityDescription | ListingAmenityDescription;
type AmenityObject = {
  key: AmenityKey;
  untranslatedLabel: AmenityDescription;
};

const Amenities: AmenityObject[] = [
  { key: 'bikeStorageAvailable', untranslatedLabel: 'Bike Storage Available' },
  { key: 'lobbyAttendant', untranslatedLabel: 'Lobby Attendant' },
  { key: 'communalOutdoorSpace', untranslatedLabel: 'Communal Outdoor Space' },
  { key: 'twentyFourSevenAccess', untranslatedLabel: '24/7 Access' },
  { key: 'gymAvailable', untranslatedLabel: 'Gym Available' },
  { key: 'barAvailable', untranslatedLabel: 'Bar Available' },
  { key: 'cafeAvailable', untranslatedLabel: 'Cafe Available' },
  { key: 'communalLobbySpace', untranslatedLabel: 'Communal Lobby Space' },
  { key: 'communalRoofAccess', untranslatedLabel: 'Communal Roof Access' },
  { key: 'foodServiceAvailable', untranslatedLabel: 'Food Service Available' },
  { key: 'generalStoreAvailable', untranslatedLabel: 'General Store Available' },
  { key: 'keyCardBuildingEntry', untranslatedLabel: 'Key Card Building Entry' },
  { key: 'lobbyBeverageAvailable', untranslatedLabel: 'Lobby Beverage Available' },
  { key: 'lobbyFoodAvailable', untranslatedLabel: 'Lobby Food Available' },
  { key: 'messengerCenter', untranslatedLabel: 'Messenger Center' },
  { key: 'parkingAvailable', untranslatedLabel: 'Parking Available' },
  { key: 'petsAllowed', untranslatedLabel: 'Pets Allowed' },
  { key: 'pharmacyAvailable', untranslatedLabel: 'Pharmacy Available' },
  { key: 'propertyManagerOnSite', untranslatedLabel: 'Property Manager On-Site' },
  { key: 'restaurantAvailable', untranslatedLabel: 'Restaurant Available' },
  { key: 'spaAvailable', untranslatedLabel: 'Spa Available' },
  { key: 'storageAvailable', untranslatedLabel: 'Storage Available' },
  { key: 'turnstiles', untranslatedLabel: 'Turnstiles' },
  { key: 'furnished', untranslatedLabel: 'Furnished' },
  { key: 'keyCardSpaceEntry', untranslatedLabel: 'Key Card Space Entry' },
  { key: 'passengerElevatorCount', untranslatedLabel: 'Passenger Elevator Count' },
  { key: 'serviceElevatorCount', untranslatedLabel: 'Service Elevator Count' },
  { key: 'twentyFourSevenSecurity', untranslatedLabel: '24/7 security' },
  { key: 'barbershop', untranslatedLabel: 'Barbershop' },
  { key: 'carWash', untranslatedLabel: 'Car wash' },
  { key: 'cctv', untranslatedLabel: 'CCTV' },
  { key: 'commonAreaWiFi', untranslatedLabel: 'Common Area WiFi' },
  { key: 'conferenceCenter', untranslatedLabel: 'Conference center' },
  { key: 'controlledElevatorAccess', untranslatedLabel: 'Controlled elevator access' },
  { key: 'coveredParking', untranslatedLabel: 'Covered parking' },
  { key: 'destinationDispatchElevators', untranslatedLabel: 'Destination dispatch elevators' },
  { key: 'evChargingStations', untranslatedLabel: 'Electric vehicle (EV) charging stations' },
  { key: 'eventSpace', untranslatedLabel: 'Event Space' },
  { key: 'flexSpaceAvailable', untranslatedLabel: 'Flex Space Available' },
  { key: 'loadingDock', untranslatedLabel: 'Loading dock' },
  { key: 'lockerRooms', untranslatedLabel: 'Locker rooms' },
  { key: 'networkingEvents', untranslatedLabel: 'Networking Events' },
  { key: 'optionToAddISP', untranslatedLabel: 'Option to Add Internet Service Provider' },
  { key: 'parkingGarage', untranslatedLabel: 'Parking garage' },
  { key: 'showers', untranslatedLabel: 'Showers' },
  { key: 'shuttle', untranslatedLabel: 'Shuttle' },
  { key: 'tenantLounge', untranslatedLabel: 'Tenant lounge' },
  { key: 'tenantPortalApp', untranslatedLabel: 'Tenant portal/app' },
  { key: 'touchFreeElevatorAccess', untranslatedLabel: 'Touch-free elevator access' },
  { key: 'touchlessTurnstileEntry', untranslatedLabel: 'Touchless turnstile entry' },
  { key: 'transportationAccessible', untranslatedLabel: 'Transportation Accessible' },
  { key: 'urgentCareDoctorOnsite', untranslatedLabel: 'Urgent Care Doctor Onsite' },
  { key: 'valetParking', untranslatedLabel: 'Valet parking' },
  { key: 'yogaStudioAvailable', untranslatedLabel: 'Yoga Studio Available' },
  { key: 'breakOutSpace', untranslatedLabel: 'Break Out Space' },
  { key: 'phoneBooths', untranslatedLabel: 'Phone Booths' },
  { key: 'privateOutdoorSpace', untranslatedLabel: 'Private outdoor space' },
  { key: 'sharedPantry', untranslatedLabel: 'Shared Pantry' },
];

export const amenityOptions: Array<{ value: AmenityDescription; label: string }> = Amenities.map(
  amenity => ({ value: amenity.untranslatedLabel, label: `filters:amenityLabels:${amenity.key}` }),
);

export const currencySymbol = (
  market: any | null,
  user: CurrentUser | null,
  locale,
  showOnlyCurrency?: boolean,
): string => {
  const userLocale = user?.languagePreference || locale || 'en-us';
  if (market) {
    const currencyCode = countryToCurrencyCode(market.countryCode);
    return currencyCodeToSymbol(currencyCode, locale, showOnlyCurrency);
  }
  return countryToCurrencySymbol('US', userLocale, showOnlyCurrency);
};

export default dateRegex;
